import { useQuery } from '@tanstack/react-query';
import { Delete, Mic, MicOff, Pause, Phone, PhoneForwarded, PhoneIncoming, PhoneOff, Play, X } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { StatusIndicator } from '@voice/ui';
import { api } from '../lib/api';
import { useSoftphone, type PhoneCall } from '../lib/softphone';
import styles from './SoftphoneDock.module.css';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

function useCallerName(call: PhoneCall): string {
  const lookup = useQuery({
    queryKey: ['contact-lookup', call.number],
    queryFn: () => api<{ caller: { name: string; company: string } | null }>(`/contacts/lookup?number=${encodeURIComponent(call.number)}`),
    enabled: call.number.length >= 3 && !call.name,
    staleTime: 5 * 60_000,
  });
  const c = lookup.data?.caller;
  return call.name || (c ? (c.company ? `${c.name} (${c.company})` : c.name) : '');
}

function Duration({ since }: { since: number | null }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (since === null) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [since]);
  if (since === null) return null;
  const s = Math.max(0, Math.floor((now - since) / 1000));
  return <span className={styles.timer}>{`${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`}</span>;
}

function IncomingBanner({ call }: { call: PhoneCall }) {
  const phone = useSoftphone();
  const name = useCallerName(call);
  return (
    <div className={styles.incoming} role="alertdialog" aria-label="Eingehender Anruf">
      <PhoneIncoming size={22} strokeWidth={1.5} aria-hidden="true" />
      <div className={styles.who}>
        <strong>{name || call.number}</strong>
        <span>{[name ? call.number : 'Eingehender Anruf', call.queue ? `Warteschlange ${call.queue}` : ''].filter(Boolean).join(' · ')}</span>
        {call.inputs.map((i) => (
          <span key={i.name}>
            {i.name.replace(/_/g, ' ')}: <strong>{i.value}</strong>
          </span>
        ))}
      </div>
      <button type="button" className={`${styles.round} ${styles.accept}`} onClick={() => void phone.accept(call.ref)} aria-label="Annehmen" autoFocus>
        <Phone size={20} strokeWidth={1.75} />
      </button>
      <button type="button" className={`${styles.round} ${styles.hang}`} onClick={() => phone.decline(call.ref)} aria-label="Ablehnen">
        <PhoneOff size={20} strokeWidth={1.75} />
      </button>
    </div>
  );
}

function CallCard({ call, others }: { call: PhoneCall; others: PhoneCall[] }) {
  const phone = useSoftphone();
  const name = useCallerName(call);
  const [transferring, setTransferring] = useState(false);
  const [target, setTarget] = useState('');
  const [dtmf, setDtmf] = useState(false);
  const live = call.state !== 'ended';
  const consult = others.find((o) => o.state === 'active' && o.ref !== call.ref);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!target.trim()) return;
    phone.transfer(call.ref, target.trim());
    setTransferring(false);
    setTarget('');
  };

  return (
    <div className={`${styles.card} ${call.held ? styles.held : ''}`}>
      <div className={styles.cardHead}>
        <div className={styles.who}>
          <strong>{name || call.number}</strong>
          <span>
            {name ? `${call.number} · ` : ''}
            {call.state === 'ended' ? call.note : call.held ? 'Gehalten' : call.state === 'active' ? 'Im Gespräch' : call.note || (call.direction === 'in' ? 'Eingehend' : 'Ausgehend')}
          </span>
        </div>
        <Duration since={call.state === 'active' ? call.answeredAt : null} />
      </div>
      {live && (call.state === 'active' || call.direction === 'out') && (
        <div className={styles.controls}>
          <button type="button" className={styles.ctl} onClick={() => phone.toggleMute(call.ref)} aria-pressed={call.muted} disabled={call.state !== 'active'}>
            {call.muted ? <MicOff size={18} strokeWidth={1.5} /> : <Mic size={18} strokeWidth={1.5} />}
            <span>{call.muted ? 'Stumm' : 'Mikrofon'}</span>
          </button>
          <button type="button" className={styles.ctl} onClick={() => phone.toggleHold(call.ref)} aria-pressed={call.held} disabled={call.state !== 'active'}>
            {call.held ? <Play size={18} strokeWidth={1.5} /> : <Pause size={18} strokeWidth={1.5} />}
            <span>{call.held ? 'Fortsetzen' : 'Halten'}</span>
          </button>
          <button type="button" className={styles.ctl} onClick={() => setTransferring((v) => !v)} aria-pressed={transferring} disabled={call.state !== 'active'}>
            <PhoneForwarded size={18} strokeWidth={1.5} />
            <span>Weiterleiten</span>
          </button>
          <button type="button" className={styles.ctl} onClick={() => setDtmf((v) => !v)} aria-pressed={dtmf} disabled={call.state !== 'active'}>
            <span className={styles.keysIcon}>123</span>
            <span>Tasten</span>
          </button>
          <button type="button" className={`${styles.ctl} ${styles.end}`} onClick={() => phone.hangup(call.ref)}>
            <PhoneOff size={18} strokeWidth={1.5} />
            <span>Auflegen</span>
          </button>
        </div>
      )}
      {dtmf && live && (
        <div className={styles.pad}>
          {KEYS.map((k) => (
            <button key={k} type="button" className={styles.key} onClick={() => phone.sendDtmf(call.ref, k)}>
              {k}
            </button>
          ))}
        </div>
      )}
      {transferring && live && (
        <form className={styles.transfer} onSubmit={submit}>
          <input value={target} onChange={(e) => setTarget(e.target.value)} placeholder="Nebenstelle oder Nummer" inputMode="tel" aria-label="Weiterleiten an" autoFocus />
          <button type="submit" className={styles.small}>
            Direkt weiterleiten
          </button>
          {consult && (
            <button
              type="button"
              className={styles.small}
              onClick={() => {
                phone.transferTo(call.ref, consult.ref);
                setTransferring(false);
              }}
            >
              Mit {consult.name || consult.number} verbinden
            </button>
          )}
        </form>
      )}
    </div>
  );
}

export function SoftphoneDock() {
  const phone = useSoftphone();
  const [open, setOpen] = useState(false);
  const [number, setNumber] = useState('');
  const incoming = phone.calls.filter((c) => c.direction === 'in' && c.state === 'ringing');
  const running = phone.calls.filter((c) => !(c.direction === 'in' && c.state === 'ringing'));

  // Beim ersten Anruf aufklappen.
  useEffect(() => {
    if (running.length > 0) setOpen(true);
  }, [running.length]);

  if (phone.status === 'none') return null;

  const press = (k: string) => {
    const active = running.find((c) => c.state === 'active');
    if (active && number === '') phone.sendDtmf(active.ref, k);
    else setNumber((n) => n + k);
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!number.trim()) return;
    void phone.dial(number);
    setNumber('');
  };
  const ready = phone.status === 'ready';

  return (
    <>
      {incoming.map((c) => (
        <IncomingBanner key={c.ref} call={c} />
      ))}
      <div className={styles.dock}>
        {open && (
          <section className={styles.panel} aria-label="Telefon">
            <header className={styles.panelHead}>
              <div>
                <h2>Telefon</h2>
                <StatusIndicator
                  state={ready ? 'ok' : phone.status === 'error' ? 'busy' : 'off'}
                  label={ready ? `Nebenstelle ${phone.extension?.number}` : phone.status === 'error' ? phone.error : phone.status === 'connecting' ? 'Verbindet …' : 'Ausgeschaltet'}
                />
              </div>
              <label className={styles.switch}>
                <input type="checkbox" checked={phone.enabled} onChange={(e) => phone.setEnabled(e.target.checked)} />
                <span>Im Browser klingeln</span>
              </label>
              <button type="button" className={styles.close} onClick={() => setOpen(false)} aria-label="Telefon schließen">
                <X size={18} strokeWidth={1.5} />
              </button>
            </header>

            <div className={styles.calls}>
              {running.map((c) => (
                <CallCard key={c.ref} call={c} others={running} />
              ))}
            </div>

            <form className={styles.dial} onSubmit={submit}>
              <input value={number} onChange={(e) => setNumber(e.target.value.replace(/[^\d+*#\s()-]/g, ''))} placeholder="Nummer eingeben" inputMode="tel" aria-label="Rufnummer" disabled={!ready} />
              {number && (
                <button type="button" className={styles.erase} onClick={() => setNumber((n) => n.slice(0, -1))} aria-label="Letzte Ziffer löschen">
                  <Delete size={18} strokeWidth={1.5} />
                </button>
              )}
              <button type="submit" className={`${styles.round} ${styles.accept}`} disabled={!ready || !number.trim()} aria-label="Anrufen">
                <Phone size={20} strokeWidth={1.75} />
              </button>
            </form>
            <div className={styles.pad}>
              {KEYS.map((k) => (
                <button key={k} type="button" className={styles.key} onClick={() => press(k)} disabled={!ready}>
                  {k}
                </button>
              ))}
            </div>
          </section>
        )}
        <button type="button" className={styles.fab} onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-label={open ? 'Telefon schließen' : 'Telefon öffnen'}>
          <Phone size={22} strokeWidth={1.75} />
          {running.length > 0 && <span className={styles.badge}>{running.length}</span>}
          <span className={`${styles.dot} ${ready ? styles.dotOk : ''}`} />
        </button>
      </div>
    </>
  );
}
